import React, { useEffect, useMemo } from "react";
import {
  useGame,
  usePlayer,
  usePlayers,
} from "@empirica/core/player/classic/react";
import { Button } from "../components/Button";
import { LossFrameDemandBarCard } from "../stage2/Stage2ResultStage";
import { InitialOfferExplanationTabs } from "./InitialOfferExplanationTabs";
import { DemandAgainstPotChart } from "./NegotiationDonutCharts";
import { dollars } from "../formatMoney";
import {
  getCounterpartCopy,
  getNegotiationTermCopy,
  isLossFrameDesign,
} from "../payoffFrame";

export function NegotiationInitialResultStage() {
  const game = useGame();
  const player = usePlayer();
  const players = usePlayers();
  const negotiation = game.get("negotiation") || {};
  const design = game.get("design") || {};
  const termCopy = getNegotiationTermCopy(design);
  const lossFrame = isLossFrameDesign(design);
  const preDiscussionAgreement = negotiation.preDiscussionAgreement;
  const initialDollarAsksByPlayerId =
    negotiation.initialDollarAsksByPlayerId || {};
  const initialExplanationsByPlayerId =
    negotiation.initialExplanationsByPlayerId || {};
  const pieDollars = Math.max(0, Number(negotiation.pieDollars ?? 0));
  const partner = players.find((candidate) => candidate.id !== player.id);
  const counterpart = getCounterpartCopy(design);
  const yourInitialOffer = Number(initialDollarAsksByPlayerId[player.id] ?? 0);
  const partnerInitialOffer = Number(
    initialDollarAsksByPlayerId[partner?.id] ?? 0
  );
  const combinedInitialOffers = yourInitialOffer + partnerInitialOffer;
  const overageDollars = Math.max(0, combinedInitialOffers - pieDollars);
  const combinedOfferPercent =
    pieDollars > 0
      ? Math.round((combinedInitialOffers / pieDollars) * 100)
      : 0;
  const initialOffersCleared = preDiscussionAgreement?.status === "agreed";
  const resultAvailable = Boolean(preDiscussionAgreement?.status);
  const allSubmitted = useMemo(
    () => players.every((candidate) => candidate.stage.get("submit")),
    [players]
  );

  useEffect(() => {
    if (initialOffersCleared) {
      player.stage.set("submit", true);
    }
  }, [initialOffersCleared, player]);

  function handleContinue() {
    player.stage.set("submit", true);
  }

  if (!resultAvailable || initialOffersCleared) {
    return (
      <div className="w-full px-8 py-6 text-center text-gray-500">
        Loading...
      </div>
    );
  }

  const alreadySubmitted = Boolean(player.stage.get("submit"));

  return (
    <div className="max-w-4xl mx-auto px-8 py-6 space-y-6">
      <div className="space-y-3">
        <h2 className="text-4xl font-semibold text-gray-900">
          Initial {termCopy.capitalizedPluralTerm} Aren&apos;t Compatible
        </h2>
        <div className="rounded-md border border-red-200 bg-red-50 p-5 space-y-2 text-2xl text-red-900">
          {lossFrame ? (
            <>
              <div>
                Together, your {termCopy.pluralTerm} totaled{" "}
                <strong>{dollars(combinedInitialOffers)}</strong>, which is{" "}
                <strong>{combinedOfferPercent}%</strong> of the{" "}
                <strong>{dollars(pieDollars)}</strong> saved amount.
              </div>
              <div>
                That is <strong>{dollars(overageDollars)}</strong> more than the
                saved amount.
              </div>
            </>
          ) : (
            <>
              <div>
                We can&apos;t give you{" "}
                <strong className="text-blue-700">
                  {dollars(yourInitialOffer)}
                </strong>{" "}
                and your {counterpart.term}{" "}
                <strong className="text-red-700">
                  {dollars(partnerInitialOffer)}
                </strong>
                .
              </div>
              <div>
                Together, your {termCopy.pluralTerm} add to{" "}
                <strong>{dollars(combinedInitialOffers)}</strong>, which is{" "}
                <strong>{combinedOfferPercent}%</strong> of the{" "}
                <strong>{dollars(pieDollars)}</strong> pot.
              </div>
              <div>
                That is <strong>{dollars(overageDollars)}</strong> more than the
                total pot.
              </div>
            </>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {lossFrame ? (
          <>
            <LossFrameDemandBarCard
              title={`Your Initial ${termCopy.capitalizedTerm}`}
              actorLabel="You"
              demand={yourInitialOffer}
              savingsAtStakeDollars={pieDollars}
              demandLabel={termCopy.savingsLabel}
              demandActionPast={termCopy.pastTense}
              demandPercentLabel={termCopy.savingsPercentLabel}
            />
            <LossFrameDemandBarCard
              title={`${counterpart.capitalizedTerm} Initial ${termCopy.capitalizedTerm}`}
              actorLabel={counterpart.capitalizedTerm}
              demand={partnerInitialOffer}
              savingsAtStakeDollars={pieDollars}
              demandLabel={termCopy.savingsLabel}
              demandActionPast={termCopy.pastTense}
              demandPercentLabel={termCopy.savingsPercentLabel}
            />
          </>
        ) : (
          <>
            <DemandAgainstPotChart
              title={`Your Initial ${termCopy.capitalizedTerm}`}
              demand={yourInitialOffer}
              pot={pieDollars}
              color="#818cf8"
              demandLabel={termCopy.yourLabel}
              remainingLabel="Pot Left Over"
              overageLabel={`${termCopy.capitalizedTerm} Above Pot`}
              totalLabel="pot"
              legendTextClassName="text-2xl"
              centerValueClassName="fill-blue-700 text-base font-semibold"
              centerTotalClassName="fill-gray-900 text-base font-semibold"
              chartOrderLabels={[
                "Pot Left Over",
                termCopy.yourLabel,
                `${termCopy.capitalizedTerm} Above Pot`,
              ]}
            />
            <DemandAgainstPotChart
              title={`${counterpart.capitalizedTerm} Initial ${termCopy.capitalizedTerm}`}
              demand={partnerInitialOffer}
              pot={pieDollars}
              color="#f87171"
              demandLabel={termCopy.counterpartLabel}
              remainingLabel="Pot Left Over"
              overageLabel={`${termCopy.capitalizedTerm} Above Pot`}
              totalLabel="pot"
              legendTextClassName="text-2xl"
              centerValueClassName="fill-red-700 text-base font-semibold"
              centerTotalClassName="fill-gray-900 text-base font-semibold"
            />
          </>
        )}
      </div>

      <InitialOfferExplanationTabs
        counterpart={counterpart}
        termCopy={termCopy}
        yourExplanation={initialExplanationsByPlayerId[player.id]}
        partnerExplanation={initialExplanationsByPlayerId[partner?.id]}
      />

      <div className="flex items-center space-x-4">
        <Button
          type="button"
          primary
          autoFocus
          disabled={alreadySubmitted}
          handleClick={handleContinue}
          className="text-xl"
        >
          Continue to Discussion
        </Button>
        {alreadySubmitted && !allSubmitted ? (
          <span className="text-sm text-gray-500">
            Waiting for your {counterpart.term}…
          </span>
        ) : null}
      </div>
    </div>
  );
}
